import {
  Button,
  DeleteIcon,
  DocumentIcon,
  EmptyState,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  TextField,
  UploadIcon,
} from '@all-manager/design-system-v2';
import { useRef } from "react";
import { DocumentMinimalData } from "../../../../types";
import DocumentItem from "../../components/DocumentItem/DocumentItem";
import useDriveFileField from "./useDriveFileField";

const ACCEPTED_FILES = "image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx";

type Props = {
  value: DocumentMinimalData | null;
  onChange: (value: DocumentMinimalData | null) => void;
  label?: string;
};

export default function DriveFileField(props: Props) {
  const {
    isDragOver,
    canUploadFiles,
    handleFileInputChange,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    handleLinkChange,
    clear,
  } = useDriveFileField(props);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const label = props.label ?? 'Document';

  // A file is already selected or uploaded.
  if (props.value) {
    if (props.value.pendingFile) {
      return (
        <Paper>
          <List disablePadding>
            <ListItem
              secondaryAction={(
                <IconButton tooltip="Retirer le fichier" color="error" onClick={clear}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            >
              <ListItemIcon>
                <DocumentIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={props.value.pendingFile.name}
                secondary="Prêt à téléverser"
                slotProps={{ primary: { noWrap: true } }}
              />
            </ListItem>
          </List>
        </Paper>
      );
    }

    return (
      <DocumentItem
        name={props.value.name ?? ''}
        url={props.value.file_url ?? ''}
        type={props.value.type ?? null}
        onDelete={clear}
      />
    );
  }

  // Drive not connected: fall back to a plain link input.
  if (!canUploadFiles) {
    return (
      <TextField
        label={`${label} (lien URL)`}
        placeholder="https://…"
        type="url"
        fullWidth
        value=""
        onChange={(e) => handleLinkChange(e.target.value)}
      />
    );
  }

  // Nothing selected yet: show the upload dropzone.
  return (
    <div onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}>
      <Paper>
        <EmptyState
          title={isDragOver ? 'Relâchez pour ajouter le fichier' : `${label} : déposez le fichier ici`}
          subtitle="Image, PDF, document ou vidéo · 10 Mo maximum"
          action={(
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadIcon />}
              onClick={() => fileInputRef.current?.click()}
            >
              Choisir un fichier
            </Button>
          )}
        />
        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept={ACCEPTED_FILES}
          onChange={handleFileInputChange}
        />
      </Paper>
    </div>
  );
}
